import Drawer from "../Drawer";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../LoaderSpinner";

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value) || 0);

const getControlTone = (product) => {
    if (product?.missingBomFlag) return "critical";
    if (product?.missingFreightFlag || product?.largeVarianceFlag) return "warning";
    if (product?.weightedCost == null || product?.weightedCost === "" || product?.weightedCost === "—") {
        return "attention";
    }
    return "ok";
};

const getControlToneClassName = (product) => {
    const tone = getControlTone(product);
    const baseClasses =
        "inline-block max-w-[136px] truncate rounded-md border px-2 py-0.5 text-[10px] font-medium shadow-none";

    if (tone === "critical") {
        return `${baseClasses} border-rose-200 bg-rose-50 text-rose-700`;
    }
    if (tone === "warning") {
        return `${baseClasses} border-amber-200 bg-amber-50 text-amber-700`;
    }
    if (tone === "attention") {
        return `${baseClasses} border-slate-200 bg-slate-100 text-slate-700`;
    }
    return `${baseClasses} border-emerald-200 bg-emerald-50 text-emerald-700`;
};

const getControlToneShortLabel = (product) => {
    const tone = getControlTone(product);
    if (tone === "critical") return "Critical";
    if (tone === "warning") return "Watch";
    if (tone === "attention") return "Pending";
    return "Governed";
};

const downloadWatchlistCsv = (rows) => {
    const headers = [
        "Execution SKU",
        "Product Name",
        "Variant",
        "Supplier",
        "Canonical Cost",
        "Control Status",
        "Last Cost Change",
    ];
    const csvRows = [
        headers.join(","),
        ...rows.map((row) =>
            [
                row.sku ?? "",
                row.name ?? row.productName ?? "",
                row.variant ?? "",
                row.supplier ?? "",
                row.canonicalCost ?? "",
                row.controlStatus ?? "",
                row.lastCostChange ?? "",
            ]
                .map(String)
                .map((value) => `"${value.replace(/"/g, '""')}"`)
                .join(",")
        ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cogs-control-watchlist.csv";
    link.click();
    URL.revokeObjectURL(url);
};

export const WatchlistDrawer = ({
    marginFromSidebar,
    drawerWidth,
    isOpen,
    onClose,
    exceptionRows = [],
    exceptionCount = 0,
    isLoading = false,
    onSelectItem,
}) => {
    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            marginFromSidebar={marginFromSidebar}
            width={drawerWidth}
            headerContent={
                <div>
                    <h2 className="font-semibold text-lg tracking-[-0.02em] text-primary">
                        Control watchlist
                    </h2>
                    <p className="text-sm mt-1 max-w-[520px] leading-6 text-gray-600">
                        Review exception SKUs without leaving the full COGS table. Select any row to inspect PO-level cost-change detail.
                    </p>
                </div>
            }
        >
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3">
                        <div>
                            <p className="text-sm font-semibold text-foreground">Open control exceptions</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                Exception rows stay accessible here while the canonical table remains the primary working surface.
                            </p>
                        </div>
                        <span className="inline-flex items-center rounded-md border border-border bg-white px-2 py-0.5 text-[11px] font-medium text-foreground shadow-none">
                            {formatNumber(exceptionCount)} open
                        </span>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={() => downloadWatchlistCsv(exceptionRows)}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-primary shadow-xs transition hover:bg-gray-50"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Export watchlist
                        </button>
                    </div>

                    <div className="space-y-3">
                        {isLoading ? (
                            <div className="flex min-h-[168px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-background px-4 py-14">
                                <LoaderSpinner width="40" height="40" border="3" colorSpinner="gray" />
                            </div>
                        ) : exceptionRows.length > 0 ? (
                            exceptionRows.map((row) => (
                                <button
                                    key={row.id ?? row.sku}
                                    type="button"
                                    className="w-full rounded-lg border border-border bg-white p-4 text-left shadow-sm transition hover:border-slate-300"
                                    onClick={() => onSelectItem?.(row)}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-lg font-semibold tracking-[-0.02em] text-foreground">
                                                {row.sku}
                                            </p>
                                            <p className="mt-1 truncate text-sm text-muted-foreground">
                                                {row.name || row.productName} · {row.variant || "Base"}
                                            </p>
                                        </div>
                                        <span className={getControlToneClassName(row)}>
                                            {getControlToneShortLabel(row)}
                                        </span>
                                    </div>
                                    <p className="mt-4 text-sm text-muted-foreground">
                                        {row.controlStatus || "—"}
                                    </p>
                                </button>
                            ))
                        ) : (
                            <div className="rounded-lg border border-dashed border-border bg-background px-4 py-10 text-center text-sm text-muted-foreground">
                                No control exceptions remain in the current filter scope.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
